import { ArrowLeft, CircleAlert, Search } from "lucide-react";
import { useEffect, useState, type FormEvent } from "react";
import { Link, useParams } from "react-router-dom";
import { usePageHeader } from "../../components/shell/PageHeaderContext";
import { Spinner } from "../../components/Spinner";
import { apiRequest } from "../../lib/api";
import { formatCalendarDate, formatTimestamp } from "../../lib/format";
import { useAdminReservation, useAdminReservations, useInvalidateAll } from "../../lib/queries";
import { REQUEST_STATUS_LABELS, amountWithCurrency, mxn, requestTimeline } from "../../lib/reservations";
import type { RequestStatus } from "../../types";
import { AuditList, Feedback, Field, KeyValues, Pill, useAction } from "./shared";

const STATUS_TONE: Record<RequestStatus, "ok" | "warn" | "bad" | "info" | "muted"> = {
  submitted: "warn", in_review: "info", proposal_ready: "ok", services_confirmed: "ok", confirmed: "ok", expired: "muted", cancelled: "bad", unavailable: "bad"
};
const STATUSES: RequestStatus[] = ["submitted", "in_review", "proposal_ready", "services_confirmed", "confirmed", "unavailable", "expired", "cancelled"];
const STYLE: Record<string, string> = { own: "Por su cuenta", group: "En grupo", private_guide: "Con guía privado", unsure: "No está seguro" };
const BUDGET: Record<string, string> = { yes: "Definido", approx: "Aproximado", guidance: "Quiere orientación" };
const FLIGHTS: Record<string, string> = { include: "Incluye vuelos internacionales", exclude: "Sin vuelos internacionales", unsure: "No está seguro" };
const CONTACT: Record<string, string> = { whatsapp: "WhatsApp", email: "Correo electrónico", call: "Llamada" };

/** The advisors' queue: every reservation and personalized request, searchable and filterable. */
export function AdminReservationsPage() {
  usePageHeader("Solicitudes de viaje", "Reservaciones y viajes a la medida: revisa, cotiza y actualiza el estado que ve el cliente.");
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("open");
  const [kind, setKind] = useState("");
  const data = useAdminReservations({ q, status, kind });
  const counts = data.data?.counts ?? {};
  const open = (counts.submitted ?? 0) + (counts.in_review ?? 0);

  return (
    <div className="page-container admin-stack">
      <div className="discover-toolbar">
        <label className="search-field"><Search size={18} aria-hidden="true" /><input type="search" value={q} onChange={(event) => setQ(event.target.value)} placeholder="Folio, viaje, cliente o correo" aria-label="Buscar solicitudes" /></label>
        <select className="standalone-input small" value={status} onChange={(event) => setStatus(event.target.value)} aria-label="Estado" style={{ width: 200 }}>
          <option value="open">Abiertas ({open})</option>
          <option value="">Todas</option>
          {STATUSES.map((value) => <option key={value} value={value}>{REQUEST_STATUS_LABELS[value]} ({counts[value] ?? 0})</option>)}
          <option value="closed">Cerradas</option>
        </select>
        <select className="standalone-input small" value={kind} onChange={(event) => setKind(event.target.value)} aria-label="Tipo" style={{ width: 180 }}>
          <option value="">Reservación y a la medida</option>
          <option value="reservation">Reservación</option>
          <option value="personalized">Viaje a la medida</option>
        </select>
      </div>
      {data.isLoading ? <div className="page-state"><Spinner label="Cargando solicitudes" /></div> : (
        <div className="table-wrap card" style={{ padding: 0 }}>
          <table className="admin-table">
            <thead><tr><th>Folio</th><th>Tipo</th><th>Cliente</th><th>Viaje</th><th>Salida</th><th>Viajeros</th><th>Monto</th><th>Estado</th><th>Actividad</th><th /></tr></thead>
            <tbody>
              {(data.data?.requests ?? []).length === 0 && <tr><td colSpan={10} className="table-empty">Sin solicitudes con esos filtros.</td></tr>}
              {(data.data?.requests ?? []).map((request) => (
                <tr key={request.id}>
                  <td translate="no"><strong>{request.folio}</strong></td>
                  <td>{request.kind === "reservation" ? "Reservación" : "A la medida"}</td>
                  <td>{request.customer.fullName}<br /><span className="request-meta">{request.customer.email}</span></td>
                  <td>{request.trip.name}{request.apartado ? <><br /><span className="pill ok">Apartado {mxn(request.apartado.balance)}</span></> : null}</td>
                  <td translate="no">{request.departureDate ? formatCalendarDate(request.departureDate) : "—"}</td>
                  <td translate="no">{request.travelers}</td>
                  <td translate="no">{request.pricing.quotedTotal !== null ? amountWithCurrency(request.pricing.quotedTotal, request.pricing.quotedCurrency ?? request.pricing.currency) : request.pricing.estimatedTotal !== null ? `≈ ${amountWithCurrency(request.pricing.estimatedTotal, request.pricing.currency)}` : "—"}</td>
                  <td><Pill label={REQUEST_STATUS_LABELS[request.status]} tone={STATUS_TONE[request.status]} /></td>
                  <td translate="no">{formatTimestamp(request.lastActivityAt)}</td>
                  <td><Link className="secondary-button small" to={`/admin/solicitudes/${request.id}`}>Abrir</Link></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/** One request: what the customer submitted (never overwritten), the proposal, the status workflow and the audit trail. */
export function AdminReservationPage() {
  const { id } = useParams();
  const detail = useAdminReservation(id);
  const invalidate = useInvalidateAll();
  const action = useAction();
  const request = detail.data?.request;
  usePageHeader(request ? `Solicitud ${request.folio}` : "Solicitud", request ? `${request.customer.fullName} · ${request.trip.name}` : undefined);
  const [status, setStatus] = useState<RequestStatus | "">("");
  const [note, setNote] = useState("");
  const [quotedTotal, setQuotedTotal] = useState("");
  const [quotedCurrency, setQuotedCurrency] = useState<"MXN" | "USD">("USD");
  const [validUntil, setValidUntil] = useState("");
  const [advisorNote, setAdvisorNote] = useState("");

  useEffect(() => {
    if (!request) return;
    setQuotedTotal(request.pricing.quotedTotal !== null ? String(request.pricing.quotedTotal) : "");
    setQuotedCurrency((request.pricing.quotedCurrency ?? request.pricing.currency) === "MXN" ? "MXN" : "USD");
    setValidUntil(request.pricing.proposalValidUntil ?? "");
    setAdvisorNote(request.advisorNote ?? "");
  }, [request?.id, request?.updatedAt]); // eslint-disable-line react-hooks/exhaustive-deps

  if (detail.isLoading) return <div className="page-state"><Spinner label="Cargando solicitud" /></div>;
  if (detail.isError || !request) return <div className="page-state error"><CircleAlert aria-hidden="true" /><strong>No encontramos esta solicitud</strong><Link className="secondary-button" to="/admin/solicitudes">Volver</Link></div>;

  const saveProposal = async (event: FormEvent) => {
    event.preventDefault();
    await action.run("proposal", async () => {
      await apiRequest(`/api/v1/admin/reservations/${request.id}`, {
        method: "PATCH",
        body: JSON.stringify({ quotedTotal: quotedTotal ? Number(quotedTotal.replace(/[^\d.]/g, "")) : null, quotedCurrency, proposalValidUntil: validUntil || null, advisorNote: advisorNote || null })
      });
      await invalidate();
      return "Propuesta y nota guardadas.";
    });
  };
  const changeStatus = async (event: FormEvent) => {
    event.preventDefault();
    if (!status) return;
    await action.run("status", async () => {
      await apiRequest(`/api/v1/admin/reservations/${request.id}`, { method: "PATCH", body: JSON.stringify({ status, note: note || undefined }) });
      await invalidate();
      setStatus(""); setNote("");
      return `Estado actualizado a «${REQUEST_STATUS_LABELS[status]}». El cliente recibe la notificación.`;
    });
  };

  const details = request.details as Record<string, unknown>;
  return (
    <div className="page-container admin-stack">
      <Link className="back-button" to="/admin/solicitudes"><ArrowLeft size={16} aria-hidden="true" /> Solicitudes</Link>
      <div className="admin-two-col">
        <div className="admin-stack">
          <section className="card">
            <div className="section-heading" style={{ margin: 0 }}>
              <div><span className="page-eyebrow">{request.kind === "reservation" ? "Solicitud de reservación" : "Viaje a la medida"}</span><h2>{request.trip.name}</h2></div>
              <Pill label={REQUEST_STATUS_LABELS[request.status]} tone={STATUS_TONE[request.status]} />
            </div>
            <KeyValues rows={[
              { label: "Folio", value: request.folio, mono: true },
              { label: "Cliente", value: `${request.customer.fullName} · ${request.customer.email}${request.customer.phone ? ` · ${request.customer.phone}` : ""}` },
              { label: "Contacto en la solicitud", value: `${request.contact.fullName ?? ""} · ${request.contact.email ?? ""} · ${request.contact.phone ?? ""}` },
              { label: "Enviada", value: formatTimestamp(request.submittedAt) },
              { label: "Salida", value: request.departureDate ? formatCalendarDate(request.departureDate, "long") : "Por definir" },
              { label: "Viajeros", value: `${request.travelers} · ${request.adults} adultos · ${request.children} menores${request.childAges.length ? ` (${request.childAges.join(", ")} años)` : ""}` },
              request.occupancyLabel ? { label: "Habitaciones", value: request.occupancyLabel } : null,
              { label: "Estimado al enviar", value: request.pricing.estimatedTotal !== null ? `${amountWithCurrency(request.pricing.estimatedTotal, request.pricing.currency)}${request.pricing.estimatedTotalMxn !== null ? ` ≈ ${mxn(request.pricing.estimatedTotalMxn)}` : ""}` : "—" },
              request.kind === "reservation" ? { label: "Pasaportes", value: request.passportConfirmed ? "Confirmados por el cliente" : "No confirmados" } : null,
              request.apartado ? { label: "Apartado", value: <Link to={`/admin/apartados/${request.apartado.id}`}>{mxn(request.apartado.balance)} · meta {mxn(request.apartado.goalAmount)} · {request.apartado.status}</Link> } : null,
              request.duplicatedFrom ? { label: "Duplicada de", value: <Link to={`/admin/solicitudes/${request.duplicatedFrom}`}>solicitud anterior</Link> } : null
            ]} />
            {request.kind === "personalized" && (
              <>
                <span className="rail-title" style={{ display: "block", marginTop: 16 }}>Preferencias</span>
                <KeyValues rows={[
                  { label: "Destinos", value: Array.isArray(details.destinations) && details.destinations.length ? (details.destinations as string[]).join(", ") : "Quiere recomendaciones" },
                  { label: "Origen", value: String(details.origin ?? "—") },
                  { label: "Fechas", value: details.datesMode === "exact" ? `${String(details.departureDate)} → ${String(details.returnDate)}` : `Flexibles · ${[details.flexiblePeriod, details.flexibleDays ? `±${details.flexibleDays} días` : null, details.durationText].filter(Boolean).join(" · ")}` },
                  { label: "Estilo", value: STYLE[String(details.style)] ?? String(details.style ?? "—") },
                  { label: "Presupuesto", value: `${BUDGET[String(details.budgetMode)] ?? "—"}${details.budgetAmount ? ` · ${Number(details.budgetAmount).toLocaleString("es-MX")} ${String(details.budgetCurrency)} ${details.budgetScope === "person" ? "por persona" : "todo el viaje"}` : ""}` },
                  { label: "Vuelos", value: FLIGHTS[String(details.flights)] ?? "—" },
                  { label: "Hospedaje", value: details.hotelPreference ? `${String(details.hotelPreference).replace("lux", " Lujo")} estrellas` : "Sin preferencia" },
                  { label: "Prefiere contacto por", value: CONTACT[String(details.contactPreference)] ?? "—" },
                  { label: "Marketing", value: details.marketingConsent ? "Acepta recibir inspiración" : "No" },
                  details.notes ? { label: "Notas del cliente", value: String(details.notes) } : null
                ]} />
              </>
            )}
          </section>

          <section className="card">
            <span className="rail-title">Seguimiento visible para el cliente</span>
            <ol className="timeline">
              {requestTimeline(request).map((step) => <li key={step.key} className={step.state}><span className="timeline-dot" aria-hidden="true" /><div><strong>{step.title}</strong><span>{step.detail}</span></div></li>)}
            </ol>
            <ul className="version-list">
              {request.timeline.map((event) => <li key={event.id}><strong>{REQUEST_STATUS_LABELS[event.toStatus]}</strong> <span className="request-meta">{event.actorType} · {formatTimestamp(event.createdAt)}{event.note ? ` · ${event.note}` : ""}</span></li>)}
            </ul>
          </section>
        </div>

        <div className="admin-stack">
          <form className="card admin-form" onSubmit={saveProposal}>
            <span className="rail-title">Propuesta del asesor</span>
            <div className="field-grid">
              <Field label="Total cotizado" htmlFor="r-quote"><input id="r-quote" className="standalone-input" inputMode="decimal" value={quotedTotal} onChange={(event) => setQuotedTotal(event.target.value)} placeholder="2890" /></Field>
              <Field label="Moneda" htmlFor="r-cur"><select id="r-cur" className="standalone-input" value={quotedCurrency} onChange={(event) => setQuotedCurrency(event.target.value as "MXN" | "USD")}><option value="USD">USD</option><option value="MXN">MXN</option></select></Field>
            </div>
            <Field label="Vigencia de la propuesta" htmlFor="r-valid" hint="Vacío: se asigna la vigencia configurada al capturar el total."><input id="r-valid" className="standalone-input" type="date" value={validUntil} onChange={(event) => setValidUntil(event.target.value)} /></Field>
            <Field label="Nota para el cliente" htmlFor="r-note" hint="Se muestra en el detalle de la solicitud."><textarea id="r-note" className="standalone-input textarea" rows={3} value={advisorNote} onChange={(event) => setAdvisorNote(event.target.value)} maxLength={2000} /></Field>
            <button type="submit" className="primary-button solid" disabled={action.busy !== null}>{action.busy === "proposal" ? <Spinner /> : null} Guardar propuesta</button>
          </form>

          <form className="card admin-form" onSubmit={changeStatus}>
            <span className="rail-title">Cambiar estado</span>
            <Field label="Nuevo estado" htmlFor="r-status">
              <select id="r-status" className="standalone-input" value={status} onChange={(event) => setStatus(event.target.value as RequestStatus)}>
                <option value="">Selecciona…</option>
                {STATUSES.filter((value) => value !== request.status).map((value) => <option key={value} value={value}>{REQUEST_STATUS_LABELS[value]}</option>)}
              </select>
            </Field>
            <Field label="Mensaje del evento" htmlFor="r-event" hint="Aparece en la línea de tiempo del cliente y en el correo."><input id="r-event" className="standalone-input" value={note} onChange={(event) => setNote(event.target.value)} maxLength={2000} /></Field>
            <Feedback error={action.error} notice={action.notice} />
            <button type="submit" className="secondary-button" disabled={!status || action.busy !== null}>{action.busy === "status" ? <Spinner /> : null} Actualizar estado</button>
            <p className="field-hint">«Propuesta lista» requiere un total cotizado. Una solicitud cancelada no puede reabrirse.</p>
            {request.apartado && (status === "unavailable" || status === "cancelled" || status === "expired" || request.statusGroup === "closed") && (
              <div className="form-error" role="alert">
                Esta solicitud tiene un Apartado con <span translate="no">{mxn(request.apartado.balance)}</span>. Al cerrarla, dispón de los fondos desde el <Link to={`/admin/apartados/${request.apartado.id}`}>Apartado</Link> (devolución a Wallet Libre) para que el dinero del cliente no quede bloqueado.
              </div>
            )}
            {request.apartado && (status === "services_confirmed" || status === "confirmed" || request.status === "confirmed") && (
              <div className="form-message" role="status">
                Servicios confirmados: aplica los fondos del <Link to={`/admin/apartados/${request.apartado.id}`}>Apartado</Link> al pago del viaje cuando Operaciones liquide al operador.
              </div>
            )}
          </form>

          <section className="card">
            <span className="rail-title">Bitácora</span>
            <AuditList events={detail.data?.audit ?? []} />
          </section>
        </div>
      </div>
    </div>
  );
}
